import { useState, useMemo, useEffect, useRef } from "react";
import { usePagination } from "@/hooks/usePagination";
import { useCountedServerPagination } from "@/hooks/useServerPagination";
import { PermissionGate } from "@/components/PermissionGate";
import { PaginationBar } from "@/components/PaginationBar";
import { useAppData } from "@/data/AppDataContext";
import type { Job, DetailedPermitStage } from "@/data/types";
import { JobService } from "@/services/job.service";
import { SettingsService } from "@/services/settings.service";
import { PERMITTING_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { usePermittingServerData } from "@/hooks/usePermittingServerPaging";
import type { PermittingQuickView } from "@/services/permitting-page.service";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import {
  Search, FileCheck, Clock, AlertTriangle, CheckCircle2, XCircle,
  MapPin, Briefcase, User, Calendar, ChevronRight, ExternalLink,
  Building2, FileText, AlertCircle, Shield, Phone, Mail,
} from "lucide-react";
import { getMunicipalityContact } from "@/data/floridaMunicipalities";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";
import { QuickViews, type QuickView } from "@/components/QuickViews";
import { formatAddress } from "@/lib/addressUtils";
import { formatShortDate, formatStandardTimestamp } from "@/lib/dateFormat";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitFilterBar, CockpitSearch,
} from "@/components/cockpit/CockpitShell";
import { MunicipalityInteractionLog } from "@/components/permitting/MunicipalityInteractionLog";
import { MunicipalityInteractionForm } from "@/components/permitting/MunicipalityInteractionForm";
import { PermitRequirementsChecklist } from "@/components/permitting/PermitRequirementsChecklist";
import { PermitRequirementsSummary } from "@/components/permitting/PermitRequirementsSummary";
import {
  appendMunicipalityInteraction,
  getMunicipalityLog,
  type MunicipalityInteractionInput,
} from "@/data/permitting/municipalityLog";
import {
  resolveJobRequirements,
  setRequirementStatus,
  setRequirementLinkUrl,
  type RequirementStatus,
} from "@/data/permitting/requirementsChecklist";
import { getRequirementProfile } from "@/data/permitting/requirements";
import { getPermitRowSummary } from "@/data/permitting/permitVisibility";
import { PermitRevisionPanel } from "@/components/permitting/PermitRevisionPanel";
import {
  resolveRevisionReadiness,
  setRevisionRequestMetadata,
  addRevisionItem,
  setRevisionItemResolved,
  getRevisionRequest,
  REVISION_SEVERITY_LABEL,
  type RevisionRequestMetadataInput,
  type RevisionItemInput,
} from "@/data/permitting/revisionRequest";
import { PermitInspectionPanel } from "@/components/permitting/PermitInspectionPanel";
import {
  resolveInspectionReadiness,
  addInspection,
  recordInspectionResult,
  clearInspectionResult,
  getInspectionLifecycle,
  type InspectionInput,
  type InspectionResultInput,
} from "@/data/permitting/inspectionLifecycle";
import { PermitCloseoutPanel } from "@/components/permitting/PermitCloseoutPanel";
import {
  resolveCloseoutReadiness,
  openPermitCloseout,
  setCloseoutMetadata,
  cycleCloseoutItem,
  getPermitCloseout,
  type CloseoutMetadataInput,
} from "@/data/permitting/permitCloseout";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { Plus, Loader2 } from "lucide-react";
import { PermitOperationalStrip, PermitOperationalChip } from "@/components/permitting/PermitOperationalStrip";
import { NtboTrackerPanel, deriveNtboSubStatus, derivePermitAssignee, NTBO_SUB_STATUS_LABEL, NTBO_SUB_STATUS_COLOR, ASSIGNEE_COLOR, PERMIT_ASSIGNEES } from "@/components/permitting/NtboTrackerPanel";
import type { PermitPrep } from "@/components/permitting/NtboTrackerPanel";

const STAGES: DetailedPermitStage[] = [
  "Permit Required", "Preparing Documents", "Submitted", "Under Review",
  "Revision Requested", "Resubmitted", "Approved", "Closed",
];

const STAGE_COLORS: Record<DetailedPermitStage, string> = {
  "Not Required": "bg-muted text-muted-foreground",
  "Permit Required": "bg-warning/15 text-warning dark:bg-warning/40 dark:text-warning",
  "Preparing Documents": "bg-info/15 text-info dark:bg-info/40 dark:text-info",
  "Submitted": "bg-primary/15 text-primary dark:bg-primary/40 dark:text-primary",
  "Under Review": "bg-primary/15 text-primary dark:bg-primary/40 dark:text-primary",
  "Revision Requested": "bg-destructive/15 text-destructive dark:bg-destructive/40 dark:text-destructive",
  "Resubmitted": "bg-info/15 text-info dark:bg-info/40 dark:text-info",
  "Approved": "bg-success/15 text-success dark:bg-success/40 dark:text-success",
  "Closed": "bg-muted text-muted-foreground",
};

const STAGE_ICONS: Record<DetailedPermitStage, React.ElementType> = {
  "Not Required": XCircle,
  "Permit Required": FileText,
  "Preparing Documents": FileText,
  "Submitted": Clock,
  "Under Review": Search,
  "Revision Requested": AlertTriangle,
  "Resubmitted": Clock,
  "Approved": CheckCircle2,
  "Closed": CheckCircle2,
};

function getSuggestedPermitStage(job: Job): DetailedPermitStage {
  const current = job.permitDetail?.detailedStage ?? "Permit Required";
  const closeout = getPermitCloseout(job);
  const closeoutReadiness = resolveCloseoutReadiness(closeout);
  if (closeoutReadiness.state === "ready") return "Closed";

  const inspection = getInspectionLifecycle(job);
  const inspectionReadiness = resolveInspectionReadiness(inspection);
  if (inspectionReadiness.status === "complete") return "Approved";
  if (inspectionReadiness.unresolvedFailures.length > 0) return "Revision Requested";

  const revision = getRevisionRequest(job);
  if (revision) {
    const revisionReadiness = resolveRevisionReadiness(revision);
    if (revisionReadiness.state === "ready") return "Resubmitted";
    return "Revision Requested";
  }

  const req = resolveJobRequirements(job);
  if (req.readiness.pending > 0) return "Preparing Documents";
  if (current === "Permit Required" || current === "Preparing Documents") return "Submitted";
  return current;
}

function getCity(job: Job): string {
  return job.address.city || "";
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function formatPermitLastUpdate(value: string | undefined | null): string {
  if (!value) return "—";
  return value.includes("T") ? formatStandardTimestamp(value) : formatShortDate(value);
}

function getJobDisplayRef(job: Job): string {
  const jobNumber = String((job as { jobNumber?: unknown }).jobNumber ?? "").trim();
  if (jobNumber && !isUuidLike(jobNumber)) return jobNumber;
  if (job.id && !isUuidLike(job.id)) return job.id;
  return "JOB-UNASSIGNED";
}

export default function PermittingPortal() {
  const appData = useAppData();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<DetailedPermitStage | "All">("All");
  const [quickView, setQuickView] = useState<PermittingQuickView>("all");
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [profileMap, setProfileMap] = useState<Map<string, string>>(new Map());
  const [interactionFormContactType, setInteractionFormContactType] = useState<"municipality" | "homeowner">("municipality");
  const [assigneeFilter, setAssigneeFilter] = useState<"all" | import("@/data/types").PermitAssignee>("all");
  const [permitFieldDraft, setPermitFieldDraft] = useState<{ municipality: string; parcelNumber: string; hasSecondBathroom: boolean } | null>(null);
  const [permitSavePendingCount, setPermitSavePendingCount] = useState(0);
  const [permitSaveLabel, setPermitSaveLabel] = useState<string>("Idle");
  const [activeSaveSections, setActiveSaveSections] = useState<Set<string>>(new Set());
  const serverPagination = useCountedServerPagination(25);
  const { setTotalItems: setServerTotalItems, reset: resetServerPagination } = serverPagination;
  const serverData = usePermittingServerData({
    enabled: PERMITTING_SERVER_PAGING_ENABLED,
    page: serverPagination.page,
    pageSize: serverPagination.pageSize,
    search,
    stage: stageFilter,
    quickView,
    branch: brFilter.branch,
    region: brFilter.region,
  });
  const jobs = PERMITTING_SERVER_PAGING_ENABLED ? serverData.jobs : appData.jobs;
  const setJobs = PERMITTING_SERVER_PAGING_ENABLED ? serverData.setJobs : appData.setJobs;
  const jobsRef = useRef<Job[]>(jobs);
  const saveSeqByJobRef = useRef<Map<string, number>>(new Map());
  const requirementSaveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    if (PERMITTING_SERVER_PAGING_ENABLED) {
      setServerTotalItems(serverData.count);
    }
  }, [serverData.count, setServerTotalItems]);

  useEffect(() => {
    return () => {
      requirementSaveTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      requirementSaveTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    SettingsService.getProfiles().then((profiles) => {
      const map = new Map<string, string>();
      profiles.forEach((p) => {
        const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ");
        if (p.id && name) map.set(p.id, name);
        if (p.auth_user_id && name) map.set(p.auth_user_id, name);
      });
      setProfileMap(map);
    }).catch(() => {});
  }, []);

  // Sync editable permit fields when a different job is selected.
  useEffect(() => {
    if (!selectedJob?.permitDetail) { setPermitFieldDraft(null); return; }
    const pd = selectedJob.permitDetail as import("@/data/types").PermitDetail;
    setPermitFieldDraft({
      municipality: pd.municipality ?? "",
      parcelNumber: pd.parcelNumber ?? "",
      hasSecondBathroom: pd.hasSecondBathroom ?? false,
    });
  }, [selectedJob?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveCoordinator = (job: Job): string => {
    const raw = job.permitDetail?.assignedCoordinator || job.salesRep || "";
    if (!raw) return "—";
    if (isUuidLike(raw)) return profileMap.get(raw) || "—";
    return raw;
  };

  const permitJobs = useMemo(
    () => (
      PERMITTING_SERVER_PAGING_ENABLED
        ? jobs
        : filterByBranchRegion(jobs.filter((j) => j.permitRequired), brFilter)
    ),
    [jobs, brFilter],
  );

  const persistPermitUpdate = async (
    jobId: string,
    updater: (job: Job) => Job,
    errorMessage: string,
    operationLabel: string,
    sectionKey: string,
  ) => {
    const target = jobsRef.current.find((j) => j.id === jobId);
    if (!target) return;
    const prevTarget = target;
    const nextSeq = (saveSeqByJobRef.current.get(jobId) ?? 0) + 1;
    saveSeqByJobRef.current.set(jobId, nextSeq);

    setPermitSavePendingCount((n) => n + 1);
    setPermitSaveLabel(operationLabel);
    setActiveSaveSections((prev) => {
      const next = new Set(prev);
      next.add(sectionKey);
      return next;
    });

    let optimistic = updater(target);
    setJobs((prev) => {
      const current = prev.find((j) => j.id === jobId);
      if (!current) return prev;
      optimistic = updater(current);
      const next = prev.map((j) => (j.id === jobId ? optimistic : j));
      jobsRef.current = next;
      return next;
    });
    setSelectedJob((prev) => (prev && prev.id === jobId ? optimistic : prev));

    const { data, error } = await JobService.update(jobId, {
      permitDetail: optimistic.permitDetail,
      permitStatus: optimistic.permitStatus,
    });
    const latestSeq = saveSeqByJobRef.current.get(jobId) ?? 0;

    // Ignore stale async responses from older writes.
    if (nextSeq < latestSeq) {
      setPermitSavePendingCount((n) => Math.max(0, n - 1));
      setActiveSaveSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionKey);
        return next;
      });
      return;
    }

    if (error || !data) {
      setJobs((prev) => {
        const next = prev.map((j) => (j.id === jobId ? prevTarget : j));
        jobsRef.current = next;
        return next;
      });
      setSelectedJob((prev) => (prev?.id === jobId ? prevTarget : prev));
      toast.error(errorMessage);
      setPermitSavePendingCount((n) => Math.max(0, n - 1));
      setPermitSaveLabel("Save failed");
      setActiveSaveSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionKey);
        return next;
      });
      return;
    }

    setJobs((prev) => {
      const next = prev.map((j) => (j.id === jobId ? data : j));
      jobsRef.current = next;
      return next;
    });
    setSelectedJob((prev) => (prev?.id === jobId ? data : prev));
    setPermitSavePendingCount((n) => Math.max(0, n - 1));
    setPermitSaveLabel("Saved");
    setActiveSaveSections((prev) => {
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    });
    if (PERMITTING_SERVER_PAGING_ENABLED) {
      void serverData.refresh();
    }
  };

  const handleAppendInteraction = async (jobId: string, input: MunicipalityInteractionInput) => {
    setShowInteractionForm(false);
    await persistPermitUpdate(
      jobId,
      (j) => appendMunicipalityInteraction(j, input),
      "Failed to save municipality interaction",
      "Saving interaction",
      "interaction",
    );
  };

  const handleSavePermitFields = async (
    jobId: string,
    fields: { municipality?: string; parcelNumber?: string; hasSecondBathroom?: boolean },
  ) => {
    await persistPermitUpdate(
      jobId,
      (j) => {
        if (!j.permitDetail) return j;
        return { ...j, permitDetail: { ...j.permitDetail, ...fields } };
      },
      "Failed to save permit fields",
      "Saving...",
      "permit-fields",
    );
  };

  const handleSetRequirement = async (
    jobId: string,
    key: string,
    status: RequirementStatus,
    updatedBy: string,
  ) => {
    // Immediate UI response.
    setJobs((prev) => prev.map((j) => (j.id === jobId ? setRequirementStatus(j, key, status, updatedBy) : j)));
    setSelectedJob((prev) =>
      prev && prev.id === jobId ? setRequirementStatus(prev, key, status, updatedBy) : prev,
    );

    // Tiny per-item debounce queue: rapid toggles collapse to one write.
    const debounceKey = `${jobId}:${key}`;
    const prior = requirementSaveTimersRef.current.get(debounceKey);
    if (prior) clearTimeout(prior);

    const timerId = setTimeout(() => {
      void persistPermitUpdate(
        jobId,
        (j) => j,
        "Failed to save permit requirement status",
        "Saving requirement",
        "requirements",
      );
      requirementSaveTimersRef.current.delete(debounceKey);
    }, 180);
    requirementSaveTimersRef.current.set(debounceKey, timerId);
  };

  const handleSetLinkUrl = async (jobId: string, key: string, url: string) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? setRequirementLinkUrl(j, key, url) : j)));
    setSelectedJob((prev) => (prev && prev.id === jobId ? setRequirementLinkUrl(prev, key, url) : prev));
    await persistPermitUpdate(
      jobId,
      (j) => j,
      "Failed to save requirement link",
      "Saving link",
      "requirements",
    );
  };

  const handleSetRevisionMetadata = async (jobId: string, input: RevisionRequestMetadataInput) =>
    persistPermitUpdate(
      jobId,
      (j) => setRevisionRequestMetadata(j, input),
      "Failed to save permit revision metadata",
      "Saving revision metadata",
      "revision",
    );
  const handleAddRevisionItem = async (jobId: string, input: RevisionItemInput) =>
    persistPermitUpdate(
      jobId,
      (j) => addRevisionItem(j, input),
      "Failed to add permit revision item",
      "Adding revision item",
      "revision",
    );
  const handleToggleRevisionItem = async (jobId: string, itemId: string, resolved: boolean, by: string) =>
    persistPermitUpdate(
      jobId,
      (j) => setRevisionItemResolved(j, itemId, resolved, by),
      "Failed to update permit revision item",
      "Updating revision item",
      "revision",
    );

  // Phase 1E inspection-lifecycle handlers
  const handleAddInspection = async (jobId: string, input: InspectionInput) =>
    persistPermitUpdate(
      jobId,
      (j) => addInspection(j, input),
      "Failed to add inspection",
      "Adding inspection",
      "inspection",
    );
  const handleRecordInspectionResult = async (jobId: string, inspectionId: string, input: InspectionResultInput) =>
    persistPermitUpdate(
      jobId,
      (j) => recordInspectionResult(j, inspectionId, input),
      "Failed to record inspection result",
      "Saving inspection result",
      "inspection",
    );
  const handleClearInspectionResult = async (jobId: string, inspectionId: string) =>
    persistPermitUpdate(
      jobId,
      (j) => clearInspectionResult(j, inspectionId),
      "Failed to clear inspection result",
      "Clearing inspection result",
      "inspection",
    );

  // Phase 1F closeout handlers
  const { repName } = useCurrentRep();
  const handleOpenCloseout = async (jobId: string) =>
    persistPermitUpdate(
      jobId,
      (j) => openPermitCloseout(j),
      "Failed to open permit closeout",
      "Opening closeout",
      "closeout",
    );
  const handleSetCloseoutMetadata = async (jobId: string, input: CloseoutMetadataInput) =>
    persistPermitUpdate(
      jobId,
      (j) => setCloseoutMetadata(j, input),
      "Failed to save permit closeout metadata",
      "Saving closeout metadata",
      "closeout",
    );
  const handleCycleCloseoutItem = async (jobId: string, itemId: string, by: string) =>
    persistPermitUpdate(
      jobId,
      (j) => cycleCloseoutItem(j, itemId, by),
      "Failed to update permit closeout checklist",
      "Updating closeout checklist",
      "closeout",
    );

  const serverSummary = PERMITTING_SERVER_PAGING_ENABLED ? serverData.summary : null;
  const localStageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    STAGES.forEach((s) => { counts[s] = 0; });
    permitJobs.forEach((j) => {
      const stage = j.permitDetail?.detailedStage || "Permit Required";
      if (counts[stage] !== undefined) counts[stage]++;
    });
    return counts;
  }, [permitJobs]);
  const stageCounts = serverSummary?.stageCounts ?? localStageCounts;

  const stalledJobs = permitJobs.filter((j) => {
    const s = j.permitDetail?.detailedStage;
    return s === "Revision Requested" || (j.permitDetail?.blockers && s !== "Approved" && s !== "Closed");
  });
  const pendingApproval = permitJobs.filter((j) => {
    const s = j.permitDetail?.detailedStage;
    return s === "Submitted" || s === "Under Review" || s === "Resubmitted";
  });
  const approvedJobs = permitJobs.filter((j) => j.permitDetail?.detailedStage === "Approved");
  const needsPrep = permitJobs.filter((j) => {
    const s = j.permitDetail?.detailedStage;
    return s === "Permit Required" || s === "Preparing Documents";
  });

  // Phase 1D: revision-aware buckets (read-only derivation).
  const revisionJobs = permitJobs.filter((j) => !!getRevisionRequest(j));
  const criticalRevisionJobs = revisionJobs.filter((j) => {
    const r = getRevisionRequest(j)!;
    return resolveRevisionReadiness(r).unresolvedCriticalCount > 0;
  });
  const readyToResubmitJobs = revisionJobs.filter((j) => {
    const r = getRevisionRequest(j)!;
    return resolveRevisionReadiness(r).state === "ready";
  });
  const waitingOnRevisionJobs = revisionJobs.filter((j) => {
    const r = getRevisionRequest(j)!;
    return resolveRevisionReadiness(r).state === "not_ready";
  });

  // Phase 1E: inspection-aware buckets (read-only derivation).
  const inspectionLifecycleJobs = permitJobs.filter((j) => !!getInspectionLifecycle(j));
  const activeInspectionJobs = inspectionLifecycleJobs.filter((j) => {
    const r = resolveInspectionReadiness(getInspectionLifecycle(j));
    return r.status === "active";
  });
  const failedInspectionJobs = inspectionLifecycleJobs.filter((j) => {
    const r = resolveInspectionReadiness(getInspectionLifecycle(j));
    return r.unresolvedFailures.length > 0;
  });
  const readyToCloseJobs = inspectionLifecycleJobs.filter((j) => {
    const r = resolveInspectionReadiness(getInspectionLifecycle(j));
    return r.status === "complete";
  });
  const awaitingFirstInspectionJobs = approvedJobs.filter((j) => !getInspectionLifecycle(j));

  // Phase 1F: closeout-aware buckets (read-only derivation).
  const closeoutOpenedJobs = permitJobs.filter((j) => !!getPermitCloseout(j));
  const closeoutReadyJobs = closeoutOpenedJobs.filter(
    (j) => resolveCloseoutReadiness(getPermitCloseout(j)).state === "ready",
  );
  const closeoutPendingJobs = closeoutOpenedJobs.filter(
    (j) => resolveCloseoutReadiness(getPermitCloseout(j)).state === "not_ready",
  );
  // "Not Started" = inspection complete OR Approved/Closed stage, but no closeout opened.
  const closeoutNotStartedJobs = permitJobs.filter((j) => {
    if (getPermitCloseout(j)) return false;
    const stage = j.permitDetail?.detailedStage;
    if (stage === "Approved" || stage === "Closed") return true;
    return resolveInspectionReadiness(getInspectionLifecycle(j)).status === "complete";
  });

  // NTBO sub-state buckets (Preparing Documents only).
  const preparingDocJobs = permitJobs.filter(
    (j) => j.permitDetail?.detailedStage === "Preparing Documents",
  );
  const awaitingSignatureJobs = preparingDocJobs.filter((j) => {
    const sub = (j.permitDetail as import("@/data/types").PermitDetail | undefined)?.ntboSubStatus ?? deriveNtboSubStatus((j.permitDetail as { prep?: PermitPrep } | undefined)?.prep);
    return sub === "AwaitingHomeownerSignature";
  });
  const awaitingNotarizationJobs = preparingDocJobs.filter((j) => {
    const sub = (j.permitDetail as import("@/data/types").PermitDetail | undefined)?.ntboSubStatus ?? deriveNtboSubStatus((j.permitDetail as { prep?: PermitPrep } | undefined)?.prep);
    return sub === "AwaitingNotarization";
  });
  const readyForSubmissionJobs = preparingDocJobs.filter((j) => {
    const sub = (j.permitDetail as import("@/data/types").PermitDetail | undefined)?.ntboSubStatus ?? deriveNtboSubStatus((j.permitDetail as { prep?: PermitPrep } | undefined)?.prep);
    return sub === "ReadyForSubmission";
  });

  const quickCounts = {
    all: serverSummary?.totalPermits ?? permitJobs.length,
    stalled: serverSummary?.stalled ?? stalledJobs.length,
    pending: serverSummary?.pendingApproval ?? pendingApproval.length,
    "needs-prep": serverSummary?.needsPrep ?? needsPrep.length,
    approved: serverSummary?.approved ?? approvedJobs.length,
    "rev-critical": serverSummary?.revCritical ?? criticalRevisionJobs.length,
    "rev-ready": serverSummary?.revReady ?? readyToResubmitJobs.length,
    "rev-waiting": serverSummary?.revWaiting ?? waitingOnRevisionJobs.length,
    "ins-active": serverSummary?.insActive ?? activeInspectionJobs.length,
    "ins-failed": serverSummary?.insFailed ?? failedInspectionJobs.length,
    "ins-ready": serverSummary?.insReady ?? readyToCloseJobs.length,
    "ins-awaiting": serverSummary?.insAwaiting ?? awaitingFirstInspectionJobs.length,
    "close-ready": serverSummary?.closeReady ?? closeoutReadyJobs.length,
    "close-pending": serverSummary?.closePending ?? closeoutPendingJobs.length,
    "close-not-started": serverSummary?.closeNotStarted ?? closeoutNotStartedJobs.length,
    "prep-awaiting-sig": awaitingSignatureJobs.length,
    "prep-awaiting-notarization": awaitingNotarizationJobs.length,
    "prep-ready-for-submission": readyForSubmissionJobs.length,
  } satisfies Record<PermittingQuickView, number>;

  const permitQuickViews: QuickView[] = [
    { key: "all", label: "All Permits", count: quickCounts.all },
    { key: "stalled", label: "Stalled / Revision", count: quickCounts.stalled, accent: "destructive" },
    { key: "pending", label: "Pending Approval", count: quickCounts.pending, accent: "warning" },
    { key: "needs-prep", label: "Needs Prep", count: quickCounts["needs-prep"], accent: "info" },
    { key: "approved", label: "Approved", count: quickCounts.approved, accent: "success" },
    { key: "rev-critical", label: "Critical Revisions", count: quickCounts["rev-critical"], accent: "destructive" },
    { key: "rev-ready", label: "Ready to Resubmit", count: quickCounts["rev-ready"], accent: "success" },
    { key: "rev-waiting", label: "Waiting on Revision Work", count: quickCounts["rev-waiting"], accent: "warning" },
    { key: "ins-active", label: "Active Inspections", count: quickCounts["ins-active"], accent: "warning" },
    { key: "ins-failed", label: "Failed Inspections", count: quickCounts["ins-failed"], accent: "destructive" },
    { key: "ins-ready", label: "Ready to Close", count: quickCounts["ins-ready"], accent: "success" },
    { key: "ins-awaiting", label: "Awaiting First Inspection", count: quickCounts["ins-awaiting"], accent: "info" },
    { key: "close-ready", label: "Closeout Ready", count: quickCounts["close-ready"], accent: "success" },
    { key: "close-pending", label: "Closeout Pending", count: quickCounts["close-pending"], accent: "warning" },
    { key: "close-not-started", label: "Closeout Not Started", count: quickCounts["close-not-started"], accent: "info" },
    { key: "prep-awaiting-sig", label: "Awaiting Homeowner Signature", count: quickCounts["prep-awaiting-sig"], accent: "warning" },
    { key: "prep-awaiting-notarization", label: "Awaiting Notarization", count: quickCounts["prep-awaiting-notarization"], accent: "warning" },
    { key: "prep-ready-for-submission", label: "Ready for Submission", count: quickCounts["prep-ready-for-submission"], accent: "success" },
  ];

  const filtered = PERMITTING_SERVER_PAGING_ENABLED ? permitJobs : permitJobs.filter((j) => {
    const stage = j.permitDetail?.detailedStage || "Permit Required";
    const matchesStage = stageFilter === "All" || stage === stageFilter;
    const matchesQuickView = (() => {
      switch (quickView) {
        case "stalled": return stalledJobs.includes(j);
        case "pending": return pendingApproval.includes(j);
        case "needs-prep": return needsPrep.includes(j);
        case "approved": return approvedJobs.includes(j);
        case "rev-critical": return criticalRevisionJobs.includes(j);
        case "rev-ready": return readyToResubmitJobs.includes(j);
        case "rev-waiting": return waitingOnRevisionJobs.includes(j);
        case "ins-active": return activeInspectionJobs.includes(j);
        case "ins-failed": return failedInspectionJobs.includes(j);
        case "ins-ready": return readyToCloseJobs.includes(j);
        case "ins-awaiting": return awaitingFirstInspectionJobs.includes(j);
        case "close-ready": return closeoutReadyJobs.includes(j);
        case "close-pending": return closeoutPendingJobs.includes(j);
        case "close-not-started": return closeoutNotStartedJobs.includes(j);
        case "prep-awaiting-sig": return awaitingSignatureJobs.includes(j);
        case "prep-awaiting-notarization": return awaitingNotarizationJobs.includes(j);
        case "prep-ready-for-submission": return readyForSubmissionJobs.includes(j);
        default: return true;
      }
    })();
     const matchesSearch =
       !search ||
       (j.id ?? '').toLowerCase().includes(search.toLowerCase()) ||
       (getJobDisplayRef(j) ?? '').toLowerCase().includes(search.toLowerCase()) ||
       (j.customerName ?? '').toLowerCase().includes(search.toLowerCase()) ||
       (formatAddress(j.address) ?? '').toLowerCase().includes(search.toLowerCase()) ||
       (j.permitDetail?.municipality ?? '').toLowerCase().includes(search.toLowerCase()) ||
       ((j.permitDetail as import("@/data/types").PermitDetail | undefined)?.parcelNumber ?? '').toLowerCase().includes(search.toLowerCase());
    const matchesAssignee = assigneeFilter === "all" || (() => {
      const pd = j.permitDetail as import("@/data/types").PermitDetail | undefined;
      const assigned = pd?.assignedTo
        ?? (pd?.detailedStage === "Preparing Documents"
          ? derivePermitAssignee((j.permitDetail as { prep?: PermitPrep } | undefined)?.prep)
          : undefined);
      return assigned === assigneeFilter;
    })();
    return matchesStage && matchesSearch && matchesQuickView && matchesAssignee;
  });

  const sorted = PERMITTING_SERVER_PAGING_ENABLED ? filtered : [...filtered].sort((a, b) => {
    const stageOrder: Record<string, number> = {
      "Revision Requested": 0, "Preparing Documents": 1, "Permit Required": 2,
      "Submitted": 3, "Under Review": 4, "Resubmitted": 5, "Approved": 6, "Closed": 7,
    };
    const sa = a.permitDetail?.detailedStage || "Permit Required";
    const sb = b.permitDetail?.detailedStage || "Permit Required";
    return (stageOrder[sa] ?? 99) - (stageOrder[sb] ?? 99);
  });
  const clientPagination = usePagination(sorted, 25);
  const pageRows = PERMITTING_SERVER_PAGING_ENABLED ? sorted : clientPagination.paged;
  const pagination = PERMITTING_SERVER_PAGING_ENABLED ? serverPagination : clientPagination;
  useEffect(() => {
    clientPagination.reset();
    resetServerPagination();
  }, [search, stageFilter, quickView, brFilter.branch, brFilter.region, assigneeFilter]);

  const stalledCount = quickCounts.stalled;
  const selectedSuggestedStage = selectedJob ? getSuggestedPermitStage(selectedJob) : null;
  const selectedCurrentStage = selectedJob?.permitDetail?.detailedStage ?? "Permit Required";
  const selectedStageConflict =
    !!selectedJob && selectedSuggestedStage !== null && selectedCurrentStage !== selectedSuggestedStage;
  const selectedRevision = selectedJob ? getRevisionRequest(selectedJob) : null;
  const selectedInspection = selectedJob ? getInspectionLifecycle(selectedJob) : null;
  const selectedCloseout = selectedJob ? getPermitCloseout(selectedJob) : null;
  const showRevisionSection =
    !!selectedJob &&
    (selectedCurrentStage === "Revision Requested" ||
      selectedCurrentStage === "Resubmitted" ||
      !!selectedRevision);
  const showInspectionSection =
    !!selectedJob &&
    (selectedCurrentStage === "Approved" ||
      selectedCurrentStage === "Closed" ||
      !!selectedInspection ||
      !!selectedRevision);
  const showCloseoutSection =
    !!selectedJob &&
    (selectedCurrentStage === "Approved" ||
      selectedCurrentStage === "Closed" ||
      !!selectedCloseout ||
      !!selectedInspection);

  const handleStageUpdate = async (jobId: string, newStage: DetailedPermitStage) => {
    const todayIso = new Date().toISOString().split("T")[0];
    const prevJobs = jobs;
    const target = jobs.find((j) => j.id === jobId);
    if (!target) return;

    // Gate: municipality + parcel number required before advancing out of "Preparing Documents".
    if (
      target.permitDetail?.detailedStage === "Preparing Documents" &&
      newStage !== "Preparing Documents"
    ) {
      const prepIdx = STAGES.indexOf("Preparing Documents");
      const newIdx = STAGES.indexOf(newStage);
      if (newIdx > prepIdx) {
        const pd = target.permitDetail;
        const missing: string[] = [];
        if (!pd.municipality?.trim()) missing.push("Municipality");
        if (!(pd as import("@/data/types").PermitDetail).parcelNumber?.trim()) missing.push("Parcel Number");
        if (missing.length > 0) {
          toast.error(`Required before advancing: ${missing.join(", ")}`);
          return;
        }
      }
    }

    const isSubmitStage = newStage === "Submitted" || newStage === "Resubmitted";
    const nextPermitDetail = target.permitDetail
      ? {
          ...target.permitDetail,
          detailedStage: newStage,
          lastUpdate: todayIso,
          submittedDate: isSubmitStage && !target.permitDetail.submittedDate
            ? todayIso
            : target.permitDetail.submittedDate,
          assignedCoordinator: target.permitDetail.assignedCoordinator || target.salesRep || "",
        }
      : {
          permitType: "",
          municipality: getCity(target),
          submittedDate: isSubmitStage ? todayIso : "",
          detailedStage: newStage,
          lastUpdate: todayIso,
          permitNotes: "",
          assignedCoordinator: target.salesRep || "",
          blockers: "",
          targetApprovalDate: "",
        };
    const nextPermitStatus =
      newStage === "Approved" ? "Approved" :
      newStage === "Revision Requested" ? "Revision Needed" :
      newStage === "Submitted" || newStage === "Resubmitted" || newStage === "Under Review" ? "Submitted" :
      target.permitStatus;

    setJobs((prev) => prev.map((j) => (
      j.id === jobId
        ? { ...j, permitDetail: nextPermitDetail, permitStatus: nextPermitStatus }
        : j
    )));
    setSelectedJob((prev) => (
      prev && prev.id === jobId
        ? { ...prev, permitDetail: nextPermitDetail, permitStatus: nextPermitStatus }
        : prev
    ));

    const { data, error } = await JobService.update(jobId, {
      permitDetail: nextPermitDetail,
      permitStatus: nextPermitStatus,
    });
    if (error || !data) {
      setJobs(prevJobs);
      setSelectedJob((prev) => (prev?.id === jobId ? target : prev));
      toast.error("Failed to save permit stage to DB");
      return;
    }

    setJobs((prev) => prev.map((j) => (j.id === jobId ? data : j)));
    setSelectedJob((prev) => (prev?.id === jobId ? data : prev));
    if (PERMITTING_SERVER_PAGING_ENABLED) {
      void serverData.refresh();
    }
  };

  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow={`Pre-Construction · ${quickCounts.all} permits tracked`}
        title="Permitting Portal"
        right={
          <div className="flex items-center gap-4">
            <HeroMetric label="Stalled" value={stalledCount} tone={stalledCount > 0 ? "bad" : "default"} />
            <HeroDivider />
            <HeroMetric label="Pending" value={quickCounts.pending} />
            <HeroDivider />
            <HeroMetric label="Approved" value={stageCounts["Approved"] || 0} tone="good" />
          </div>
        }
      />

      <QuickViews views={permitQuickViews} active={quickView} onChange={(key) => setQuickView(key as PermittingQuickView)} />

      {/* Stage pipeline — hairline tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 border-y border-border/60 divide-x divide-border/60">
        {STAGES.map((stage) => {
          const Icon = STAGE_ICONS[stage];
          const isActive = stageFilter === stage;
          const count = stageCounts[stage] || 0;
          const isAlert = stage === "Revision Requested" && count > 0;
          return (
            <button
              key={stage}
              onClick={() => setStageFilter(isActive ? "All" : stage)}
              className={`px-3 py-2.5 text-left transition-colors ${
                isActive ? "bg-primary/[0.06]" : "hover:bg-muted/30"
              }`}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className={`h-3 w-3 ${isAlert ? "text-destructive" : "text-muted-foreground/70"}`} />
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 truncate">{stage}</span>
              </div>
              <p className={`text-lg font-semibold tabular-nums ${isAlert ? "text-destructive" : "text-foreground"}`}>{count}</p>
            </button>
          );
        })}
      </div>

      <CockpitFilterBar
        search={
          <CockpitSearch
            value={search}
            onChange={setSearch}
            placeholder="Search by job, customer, address, or municipality…"
          />
        }
        right={
          <div className="flex items-center gap-2">
            <Select value={assigneeFilter} onValueChange={(v) => setAssigneeFilter(v as "all" | import("@/data/types").PermitAssignee)}>
              <SelectTrigger className="h-7 w-[150px] text-xs">
                <SelectValue placeholder="All Assignees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignees</SelectItem>
                {PERMIT_ASSIGNEES.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <BranchRegionFilter
              value={brFilter}
              onChange={setBrFilter}
              jobs={PERMITTING_SERVER_PAGING_ENABLED ? [] : jobs}
              branches={serverSummary?.branches}
              regions={serverSummary?.regions}
              compact
            />
          </div>
        }
      />

      {/* Permits Table — hairline */}
      <div className="border-t border-border/60">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider">Job</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider">Customer</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider hidden lg:table-cell">Municipality</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider hidden md:table-cell">Type</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider">Stage</th>
                <th className="py-2.5 px-3 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Assigned To</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider hidden md:table-cell tabular-nums">Submitted</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider hidden lg:table-cell tabular-nums">Last Update</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider hidden xl:table-cell">Coordinator</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground/70 uppercase text-[10px] tracking-wider hidden lg:table-cell">Blockers</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((job) => {
                const pd = job.permitDetail;
                const stage = pd?.detailedStage || "Permit Required";
                const Icon = STAGE_ICONS[stage];
                const hasBlocker = !!pd?.blockers;
                const summary = getPermitRowSummary(job);
                const readinessTone =
                  summary.readinessPct === null ? "text-muted-foreground" :
                  summary.readinessPct >= 100 ? "text-success" :
                  summary.openRequirements > 0 ? "text-warning" : "text-foreground";
                const stageAgeTone =
                  summary.stageAge.tone === "red" ? "text-destructive" :
                  summary.stageAge.tone === "amber" ? "text-warning" :
                  "text-muted-foreground";
                const revisionRequest = getRevisionRequest(job);
                const revisionReadiness = revisionRequest ? resolveRevisionReadiness(revisionRequest) : null;
                const revisionTone =
                  revisionReadiness?.tone === "red" ? "text-destructive" :
                  revisionReadiness?.tone === "green" ? "text-success" :
                  revisionReadiness?.tone === "amber" ? "text-warning" :
                  "text-muted-foreground";
                const inspectionLifecycle = getInspectionLifecycle(job);
                const inspectionReadiness = inspectionLifecycle
                  ? resolveInspectionReadiness(inspectionLifecycle)
                  : null;
                const inspectionTone =
                  inspectionReadiness?.tone === "red" ? "text-destructive" :
                  inspectionReadiness?.tone === "green" ? "text-success" :
                  inspectionReadiness?.tone === "amber" ? "text-warning" :
                  "text-muted-foreground";
                const showAwaitingFirstInspection =
                  stage === "Approved" && !inspectionLifecycle;
                const closeout = getPermitCloseout(job);
                const closeoutReadiness = closeout ? resolveCloseoutReadiness(closeout) : null;
                const closeoutTone =
                  closeoutReadiness?.tone === "green" ? "text-success" :
                  closeoutReadiness?.tone === "amber" ? "text-warning" :
                  "text-muted-foreground";
                const showCloseoutNotStarted =
                  !closeout &&
                  (stage === "Approved" || stage === "Closed" ||
                    (inspectionReadiness?.status === "complete"));
                return (
                  <tr
                    key={job.id}
                    className="border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => setSelectedJob(job)}
                  >
                    <td className="py-2.5 px-3">
                      <Link
                        to={`/jobs/${job.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-primary hover:underline tabular-nums"
                      >
                        {getJobDisplayRef(job)}
                      </Link>
                    </td>
                    <td className="py-2.5 px-3">
                      <p className="font-medium text-foreground">{job.customerName}</p>
                      <p className="text-[10px] text-muted-foreground/70">{job.projectType}</p>
                    </td>
                    <td className="py-2.5 px-3 hidden lg:table-cell">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {pd?.municipality || getCity(job)}
                        </span>
                        {(pd as import("@/data/types").PermitDetail | undefined)?.parcelNumber && (
                          <span className="text-[10px] text-muted-foreground/70 tabular-nums pl-4">
                            {(pd as import("@/data/types").PermitDetail).parcelNumber}
                          </span>
                        )}
                        <span
                          className={`text-[10px] flex items-center gap-1 ${
                            summary.usesGenericProfile ? "text-warning" : "text-muted-foreground/70"
                          }`}
                          title={summary.profileLabel}
                        >
                          {summary.usesGenericProfile && <AlertTriangle className="h-2.5 w-2.5" />}
                          {summary.usesGenericProfile ? "Generic fallback" : `${summary.profileLabel}`}
                        </span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 hidden md:table-cell text-muted-foreground">
                      {pd?.permitType || "—"}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded w-fit ${STAGE_COLORS[stage]}`}>
                          <Icon className="h-3 w-3" />
                          {stage}
                        </span>
                        <div className="space-y-0.5 text-[10px] leading-tight">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className={`tabular-nums font-medium ${readinessTone}`}>
                              {summary.readinessPct === null ? "Checklist: N/A" : `${summary.readinessPct}% ready`}
                            </span>
                            {summary.openRequirements > 0 && (
                              <span className="text-muted-foreground tabular-nums">{summary.openRequirements} open</span>
                            )}
                            {summary.stageAge.days !== null && (
                              <span className={`tabular-nums ${stageAgeTone}`}>{summary.stageAge.label}</span>
                            )}
                          </div>

                          {summary.interactionCount > 0 && (
                            <div className="text-muted-foreground tabular-nums">
                              {summary.interactionCount} interaction{summary.interactionCount === 1 ? "" : "s"}
                            </div>
                          )}

                          {summary.overdueFollowUpCount > 0 && (
                            <div className="text-destructive flex items-center gap-1">
                              <AlertCircle className="h-2.5 w-2.5" />
                              Follow-up overdue
                            </div>
                          )}

                          {revisionRequest && revisionReadiness && (
                            <div className={`flex items-center gap-1 tabular-nums ${revisionTone}`}>
                              {revisionReadiness.tone === "red" ? "🔴 " :
                                revisionReadiness.tone === "green" ? "🟢 " :
                                revisionReadiness.tone === "amber" ? "🟡 " : ""}
                              {REVISION_SEVERITY_LABEL[revisionRequest.severity]} revision: {revisionReadiness.progressLabel}
                            </div>
                          )}

                          {inspectionReadiness && (
                            <div className={`flex items-center gap-1 tabular-nums ${inspectionTone}`}>
                              {inspectionReadiness.tone === "red" ? "🔴 " :
                                inspectionReadiness.tone === "green" ? "🟢 " :
                                inspectionReadiness.tone === "amber" ? "🟡 " : ""}
                              {inspectionReadiness.status === "complete"
                                ? "Ready for permit close"
                                : inspectionReadiness.unresolvedFailures.length > 0
                                  ? `Inspection failure: ${inspectionReadiness.unresolvedFailures[0].inspectionType}`
                                  : `Inspections active: ${inspectionReadiness.progressLabel}`}
                            </div>
                          )}

                          {showAwaitingFirstInspection && (
                            <div className="text-info tabular-nums">Awaiting first inspection</div>
                          )}

                          {closeoutReadiness && (
                            <div className={`flex items-center gap-1 tabular-nums ${closeoutTone}`}>
                              {closeoutReadiness.tone === "green" ? "🟢 " :
                                closeoutReadiness.tone === "amber" ? "🟡 " : "⚪ "}
                              {closeoutReadiness.state === "ready"
                                ? `Closeout ready: ${closeoutReadiness.progressLabel}`
                                : `Closeout pending: ${closeoutReadiness.progressLabel}`}
                            </div>
                          )}

                          {showCloseoutNotStarted && (
                            <div className="text-muted-foreground tabular-nums">⚪ Closeout not started</div>
                          )}
                          <div className="pt-0.5">
                            <PermitOperationalChip job={job} variant="summary" />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 hidden md:table-cell">
                      {(() => {
                        const pdTyped = pd as import("@/data/types").PermitDetail | undefined;
                        if (stage !== "Preparing Documents") return <span className="text-muted-foreground/50">—</span>;
                        const assignee = pdTyped?.assignedTo ?? derivePermitAssignee((job.permitDetail as { prep?: PermitPrep } | undefined)?.prep);
                        return (
                          <Badge variant="outline" className={`text-[10px] ${ASSIGNEE_COLOR[assignee]}`}>
                            {assignee}
                          </Badge>
                        );
                      })()}
                    </td>
                    <td className="py-2.5 px-3 hidden md:table-cell text-muted-foreground tabular-nums">
                      {pd?.submittedDate ||
                        job.activityHistory?.find(a => /submitted/i.test(a.action))?.date?.split("T")[0] ||
                        "—"}
                    </td>
                    <td className="py-2.5 px-3 hidden lg:table-cell text-muted-foreground tabular-nums">
                      {formatPermitLastUpdate(pd?.lastUpdate)}
                    </td>
                    <td className="py-2.5 px-3 hidden xl:table-cell text-muted-foreground">
                      {resolveCoordinator(job)}
                    </td>
                    <td className="py-2.5 px-3 hidden lg:table-cell">
                      {(pd?.blockers || job.holdReason) ? (
                        <span className="text-destructive text-[11px] flex items-center gap-1 max-w-[200px] truncate">
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          {pd?.blockers || job.holdReason}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationBar {...pagination} />
        {sorted.length === 0 && (
          <EmptyState
            icon={FileText}
            title="No permits in tracking"
            description="Permits appear here once a job enters pre-construction and requires municipal approval."
            actionLabel="View Jobs"
            onAction={() => navigate("/jobs")}
          />
        )}
      </div>

      {/* Permit Detail Dialog */}
      {selectedJob && (
        <Dialog open={!!selectedJob} onOpenChange={() => { setSelectedJob(null); setShowInteractionForm(false); }}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
            {/* ─── Header ─── */}
            <DialogHeader className="px-6 pt-6 pb-4 border-b">
              <div className="flex items-center gap-3">
                <DialogTitle className="text-lg">Permit — {getJobDisplayRef(selectedJob)}</DialogTitle>
                {selectedJob.permitDetail && (
                  <Badge variant="outline" className={STAGE_COLORS[selectedJob.permitDetail.detailedStage]}>
                    {selectedJob.permitDetail.detailedStage}
                  </Badge>
                )}
                {(() => {
                  const pdTyped = selectedJob.permitDetail as import("@/data/types").PermitDetail | undefined;
                  const assignee = pdTyped?.assignedTo
                    ?? (selectedJob.permitDetail?.detailedStage === "Preparing Documents"
                      ? derivePermitAssignee((selectedJob.permitDetail as { prep?: PermitPrep } | undefined)?.prep)
                      : undefined);
                  if (!assignee) return null;
                  return (
                    <Badge variant="outline" className={ASSIGNEE_COLOR[assignee]}>
                      {assignee}
                    </Badge>
                  );
                })()}
                <div className="ml-auto w-[170px] flex justify-end">
                  <Badge
                    variant="outline"
                    className={`inline-flex w-[170px] justify-center ${
                      permitSavePendingCount > 0
                        ? "text-warning border-warning/40"
                        : "text-success border-success/40"
                    }`}
                  >
                    {permitSavePendingCount > 0 ? "Saving changes..." : "Saved"}
                  </Badge>
                </div>
              </div>
            </DialogHeader>

            <div className="px-6 py-5 space-y-5">
              {/* ─── 1. Job Context ─── */}
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="font-semibold text-foreground">{selectedJob.customerName}</p>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                  <MapPin className="h-3.5 w-3.5" />
                  {formatAddress(selectedJob.address)}
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{selectedJob.projectType}</span>
                  <StatusBadge type="stage" value={selectedJob.currentStage} />
                  <span>{selectedJob.branch}</span>
                </div>
              </div>

              {/* ─── 2. Blocker (highest urgency, surface near top) ─── */}
              {selectedJob.permitDetail?.blockers && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-destructive mb-1">
                    <AlertTriangle className="h-4 w-4" />
                    Blocker
                  </div>
                  <p className="text-sm text-foreground">{selectedJob.permitDetail.blockers}</p>
                </div>
              )}

              {/* ─── 2a. Operational Signal Strip ─── */}
              <PermitOperationalStrip job={selectedJob} />

              {/* ─── 2b. Revision Management (Phase 1D) ─── */}
              {showRevisionSection && (
                <div className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center justify-between h-5">
                    <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      Revision Management
                    </h4>
                    <span
                      className={`text-[11px] inline-flex items-center gap-1 transition-opacity ${
                        activeSaveSections.has("revision")
                          ? "opacity-100 text-warning"
                          : "opacity-0"
                      }`}
                      aria-hidden={!activeSaveSections.has("revision")}
                    >
                      <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                    </span>
                  </div>
                  <PermitRevisionPanel
                    job={selectedJob}
                    onSetMetadata={handleSetRevisionMetadata}
                    onAddItem={handleAddRevisionItem}
                    onToggleItem={handleToggleRevisionItem}
                  />
                </div>
              )}

              {/* ─── 2c. Inspection Lifecycle (Phase 1E) ─── */}
              {showInspectionSection && (
                <div className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center justify-between h-5">
                    <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      Inspection Lifecycle
                    </h4>
                    <span
                      className={`text-[11px] inline-flex items-center gap-1 transition-opacity ${
                        activeSaveSections.has("inspection")
                          ? "opacity-100 text-warning"
                          : "opacity-0"
                      }`}
                      aria-hidden={!activeSaveSections.has("inspection")}
                    >
                      <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                    </span>
                  </div>
                  <PermitInspectionPanel
                    job={selectedJob}
                    onAddInspection={handleAddInspection}
                    onRecordResult={handleRecordInspectionResult}
                    onClearResult={handleClearInspectionResult}
                  />
                </div>
              )}

              {/* ─── 2d. Permit Closeout Readiness (Phase 1F) ─── */}
              {showCloseoutSection && (
                <div className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center justify-between h-5">
                    <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      Permit Closeout Readiness
                    </h4>
                    <span
                      className={`text-[11px] inline-flex items-center gap-1 transition-opacity ${
                        activeSaveSections.has("closeout")
                          ? "opacity-100 text-warning"
                          : "opacity-0"
                      }`}
                      aria-hidden={!activeSaveSections.has("closeout")}
                    >
                      <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                    </span>
                  </div>
                  <PermitCloseoutPanel
                    job={selectedJob}
                    onOpenCloseout={handleOpenCloseout}
                    onSetMetadata={handleSetCloseoutMetadata}
                    onCycleItem={handleCycleCloseoutItem}
                    actor={repName}
                  />
                </div>
              )}

              {/* ─── 3. Permit Information + Municipality Contact (two-column) ─── */}
              {selectedJob.permitDetail && (() => {
                const contact = getMunicipalityContact(selectedJob.permitDetail.municipality);
                const isPreparing = selectedJob.permitDetail.detailedStage === "Preparing Documents";
                const draftMunicipality = permitFieldDraft?.municipality ?? selectedJob.permitDetail.municipality ?? "";
                const draftParcel = permitFieldDraft?.parcelNumber ?? (selectedJob.permitDetail as import("@/data/types").PermitDetail).parcelNumber ?? "";
                const draftHasSecondBathroom = permitFieldDraft?.hasSecondBathroom ?? (selectedJob.permitDetail as import("@/data/types").PermitDetail).hasSecondBathroom ?? false;
                const municipalityMissing = isPreparing && !draftMunicipality.trim();
                const parcelMissing = isPreparing && !draftParcel.trim();
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left: permit metadata */}
                    <div className="rounded-lg border p-4">
                      <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Permit Information</h4>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
                        {/* Municipality — editable */}
                        <div>
                          <label className={`text-[11px] ${municipalityMissing ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                            Municipality{isPreparing ? " *" : ""}
                          </label>
                          <Input
                            className={`mt-0.5 h-7 text-xs ${municipalityMissing ? "border-destructive/60 focus-visible:ring-destructive/30" : ""}`}
                            value={draftMunicipality}
                            placeholder="e.g. City of Orlando"
                            onChange={(e) => setPermitFieldDraft((prev) => ({ ...(prev ?? { municipality: "", parcelNumber: "", hasSecondBathroom: false }), municipality: e.target.value }))}
                            onBlur={() => {
                              if (draftMunicipality !== selectedJob.permitDetail!.municipality) {
                                void handleSavePermitFields(selectedJob.id, { municipality: draftMunicipality });
                              }
                            }}
                          />
                          {municipalityMissing && (
                            <p className="text-[10px] text-destructive mt-0.5">Required before advancing stage</p>
                          )}
                        </div>
                        {/* Parcel Number — new editable field */}
                        <div>
                          <label className={`text-[11px] ${parcelMissing ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                            Parcel Number{isPreparing ? " *" : ""}
                          </label>
                          <Input
                            className={`mt-0.5 h-7 text-xs ${parcelMissing ? "border-destructive/60 focus-visible:ring-destructive/30" : ""}`}
                            value={draftParcel}
                            placeholder="e.g. 12-34-56-789012"
                            onChange={(e) => setPermitFieldDraft((prev) => ({ ...(prev ?? { municipality: "", parcelNumber: "", hasSecondBathroom: false }), parcelNumber: e.target.value }))}
                            onBlur={() => {
                              if (draftParcel !== ((selectedJob.permitDetail as import("@/data/types").PermitDetail).parcelNumber ?? "")) {
                                void handleSavePermitFields(selectedJob.id, { parcelNumber: draftParcel });
                              }
                            }}
                          />
                          {parcelMissing && (
                            <p className="text-[10px] text-destructive mt-0.5">Required before advancing stage</p>
                          )}
                        </div>
                        {/* Second Bathroom flag — shows conditional floor plan checklist item */}
                        <div className="col-span-2 flex items-center gap-2 pt-0.5">
                          <Checkbox
                            id="hasSecondBathroom"
                            checked={draftHasSecondBathroom}
                            onCheckedChange={(checked) => {
                              const val = checked === true;
                              setPermitFieldDraft((prev) =>
                                prev ? { ...prev, hasSecondBathroom: val } : { municipality: "", parcelNumber: "", hasSecondBathroom: val },
                              );
                              void handleSavePermitFields(selectedJob.id, { hasSecondBathroom: val });
                            }}
                          />
                          <Label htmlFor="hasSecondBathroom" className="text-xs cursor-pointer">
                            Scope includes a second bathroom (adds floor plan checklist item)
                          </Label>
                        </div>
                        <div>
                          <p className="text-[11px] text-muted-foreground">Type</p>
                          <p className="font-medium text-foreground">{selectedJob.permitDetail.permitType}</p>
                        </div>
                        <div>
                          <p className="text-[11px] text-muted-foreground">Coordinator</p>
                          <p className="font-medium text-foreground flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            {resolveCoordinator(selectedJob)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] text-muted-foreground">Submitted</p>
                          <p className="font-medium text-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {selectedJob.permitDetail.submittedDate || "Not yet submitted"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] text-muted-foreground">Target Approval</p>
                          <p className="font-medium text-foreground">
                            {selectedJob.permitDetail.targetApprovalDate || "TBD"}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-[11px] text-muted-foreground">Last Update</p>
                          <p className="font-medium text-foreground">{formatPermitLastUpdate(selectedJob.permitDetail.lastUpdate)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Right: municipality contact (call / email actions) */}
                    <div className="rounded-lg border p-4">
                      <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Municipality Contact</h4>
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <p className="font-medium text-foreground text-sm leading-tight">
                          {contact?.name ?? selectedJob.permitDetail.municipality}
                        </p>
                      </div>

                      {contact ? (
                        <div className="space-y-2">
                          <a
                            href={`tel:${contact.phone}`}
                            className="flex items-center gap-2 rounded-md border bg-background hover:bg-accent transition-colors px-3 py-2 text-sm group"
                          >
                            <Phone className="h-3.5 w-3.5 text-primary" />
                            <span className="font-medium text-foreground tabular-nums">{contact.phoneDisplay}</span>
                            <span className="ml-auto text-[11px] text-muted-foreground group-hover:text-foreground">Call</span>
                          </a>
                          <a
                            href={`mailto:${contact.email}?subject=${encodeURIComponent(`Permit inquiry — ${getJobDisplayRef(selectedJob)} (${selectedJob.customerName})`)}`}
                            className="flex items-center gap-2 rounded-md border bg-background hover:bg-accent transition-colors px-3 py-2 text-sm group"
                          >
                            <Mail className="h-3.5 w-3.5 text-primary" />
                            <span className="font-medium text-foreground truncate">{contact.email}</span>
                            <span className="ml-auto text-[11px] text-muted-foreground group-hover:text-foreground shrink-0">Email</span>
                          </a>
                          {contact.website && (
                            <a
                              href={contact.website}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-2 text-[11px] text-muted-foreground hover:text-primary transition-colors px-1 pt-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Department portal
                            </a>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">
                          No contact on file for this municipality. Verify against county website before escalating.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ─── 3a. NTBO Tracker ─── */}
              {selectedJob && (
                <NtboTrackerPanel
                  key={selectedJob.id}
                  job={selectedJob}
                  onSave={(updatedJob) => {
                    setSelectedJob(updatedJob);
                    setJobs(prev => prev.map(j => j.id === updatedJob.id ? updatedJob : j));
                  }}
                />
              )}

              {/* ─── 4. Notes ─── */}
              {selectedJob.permitDetail?.permitNotes && (
                <div>
                  <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-3 border">
                    {selectedJob.permitDetail.permitNotes}
                  </p>
                </div>
              )}

              {/* ─── 4a. Permit Requirements Checklist (Phase 1B) ─── */}
              {selectedJob.permitDetail && (() => {
                const { items, readiness } = resolveJobRequirements(selectedJob);
                const profile = getRequirementProfile(selectedJob.permitDetail.municipality);
                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between h-5">
                      <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        Permit Requirements
                      </h4>
                      <span
                        className={`text-[11px] inline-flex items-center gap-1 transition-opacity ${
                          activeSaveSections.has("requirements")
                            ? "opacity-100 text-warning"
                            : "opacity-0"
                        }`}
                        aria-hidden={!activeSaveSections.has("requirements")}
                      >
                        <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                      </span>
                    </div>
                    <PermitRequirementsSummary readiness={readiness} profileLabel={profile.label} />
                    <PermitRequirementsChecklist
                      job={selectedJob}
                      items={items}
                      onSetStatus={handleSetRequirement}
                      onSetLinkUrl={handleSetLinkUrl}
                    />
                  </div>
                );
              })()}

              {/* ─── 4b. Municipality Interaction Log (Phase 1A) ─── */}
              {selectedJob.permitDetail && (() => {
                const log = getMunicipalityLog(selectedJob);
                const summary = getPermitRowSummary(selectedJob);
                return (
                  <div>
                    {/* Phase 1C: compact operational summary strip */}
                    <div className="mb-3 rounded-md border bg-muted/20 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
                      <span>
                        <span className="text-muted-foreground">Readiness </span>
                        <span className="tabular-nums font-medium text-foreground">
                          {summary.readinessPct === null ? "N/A" : `${summary.readinessPct}%`}
                        </span>
                      </span>
                      <span>
                        <span className="text-muted-foreground">Open </span>
                        <span className="tabular-nums font-medium text-foreground">{summary.openRequirements}</span>
                      </span>
                      <span>
                        <span className="text-muted-foreground">Interactions </span>
                        <span className="tabular-nums font-medium text-foreground">{summary.interactionCount}</span>
                      </span>
                      {summary.stageAge.days !== null && (
                        <span>
                          <span className="text-muted-foreground">Stage age </span>
                          <span
                            className={`tabular-nums font-medium ${
                              summary.stageAge.tone === "red" ? "text-destructive" :
                              summary.stageAge.tone === "amber" ? "text-warning" :
                              "text-foreground"
                            }`}
                          >
                            {summary.stageAge.label}
                          </span>
                        </span>
                      )}
                      {summary.overdueFollowUpCount > 0 && (
                        <span className="text-destructive flex items-center gap-1 font-medium">
                          <AlertCircle className="h-3 w-3" /> {summary.overdueFollowUpCount} follow-up overdue
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        Interactions
                      </h4>
                      <span
                        className={`text-[11px] inline-flex items-center gap-1 mr-2 transition-opacity ${
                          activeSaveSections.has("interaction")
                            ? "opacity-100 text-warning"
                            : "opacity-0"
                        }`}
                        aria-hidden={!activeSaveSections.has("interaction")}
                      >
                        <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                      </span>
                      {!showInteractionForm && (
                        <PermissionGate permission="jobs_approve">
                          <div className="flex items-center gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 gap-1 text-xs"
                              onClick={() => { setInteractionFormContactType("homeowner"); setShowInteractionForm(true); }}
                            >
                              <Plus className="h-3 w-3" /> Log Call
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 gap-1 text-xs"
                              onClick={() => { setInteractionFormContactType("municipality"); setShowInteractionForm(true); }}
                            >
                              <Plus className="h-3 w-3" /> Log Interaction
                            </Button>
                          </div>
                        </PermissionGate>
                      )}
                    </div>
                    {showInteractionForm && (
                      <div className="mb-3">
                        <MunicipalityInteractionForm
                          municipality={selectedJob.permitDetail.municipality}
                          defaultContactType={interactionFormContactType}
                          onSubmit={(input) => handleAppendInteraction(selectedJob.id, input)}
                          onCancel={() => setShowInteractionForm(false)}
                        />
                      </div>
                    )}
                    <MunicipalityInteractionLog log={log} showFilters />
                  </div>
                );
              })()}

              {/* ─── 5. Stage Progression ─── */}
              {selectedJob.permitDetail && (
                <div>
                  <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Permit Progress</h4>
                  <div className="flex items-center gap-1 flex-wrap">
                    {STAGES.map((stage, idx) => {
                      const currentIdx = STAGES.indexOf(selectedJob.permitDetail!.detailedStage);
                      const isCurrent = stage === selectedJob.permitDetail!.detailedStage;
                      const isPast = idx < currentIdx;
                      return (
                        <div key={stage} className="flex items-center gap-1">
                          <div
                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium border ${
                              isCurrent
                                ? `${STAGE_COLORS[stage]} border-current font-bold`
                                : isPast
                                ? "bg-success/15 text-success border-success/30 dark:bg-success/30 dark:text-success dark:border-success/70"
                                : "bg-muted text-muted-foreground border-border"
                            }`}
                          >
                            {stage}
                          </div>
                          {idx < STAGES.length - 1 && (
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ─── Footer: Update stage + Open job (separated, side-by-side) ─── */}
            <div className="border-t bg-muted/20 px-6 py-4 flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1 min-w-0">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Update Permit Stage</Label>
                {selectedJob && selectedSuggestedStage && (
                  <div className="mt-1 flex items-center gap-2 text-[11px]">
                    <span className="text-muted-foreground">Suggested:</span>
                    <Badge variant="outline" className={STAGE_COLORS[selectedSuggestedStage]}>
                      {selectedSuggestedStage}
                    </Badge>
                    {selectedStageConflict && (
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => void handleStageUpdate(selectedJob.id, selectedSuggestedStage)}
                      >
                        Apply suggestion
                      </button>
                    )}
                  </div>
                )}
                {selectedStageConflict && (
                  <div className="mt-1 text-[11px] text-warning">
                    Manual stage differs from system-derived checklist/inspection/closeout status.
                  </div>
                )}
                <Select
                  value={selectedJob.permitDetail?.detailedStage || "Permit Required"}
                  onValueChange={(v) => handleStageUpdate(selectedJob.id, v as DetailedPermitStage)}
                  disabled={permitSavePendingCount > 0}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Link
                to={`/jobs/${selectedJob.id}`}
                onClick={() => setSelectedJob(null)}
                className="sm:shrink-0"
              >
                <Button variant="outline" className="w-full sm:w-auto gap-2 text-sm">
                  <ExternalLink className="h-4 w-4" />
                  View Full Job Detail
                </Button>
              </Link>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </CockpitPage>
  );
}
